import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import sparklesUrl from '../../../assets/customIcons/sparkles.svg'
import { useLoadingMessage } from './hooks/useLoadingMessage'

const SPARKLE_SIZE = 36

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 'var(--gap-lg)'
  },
  // The sparkle shape is supplied by a CSS mask of the icon SVG. Inside,
  // a child div carries a multi-color linear gradient and rotates --
  // because the mask clips to the sparkle silhouette, the rotating
  // gradient looks like it's sweeping through the sparkle itself rather
  // than around it. Outer pulses gently; inner rotates continuously.
  sparkleContainer: {
    position: 'relative',
    width: SPARKLE_SIZE,
    height: SPARKLE_SIZE,
    overflow: 'hidden',
    WebkitMaskImage: `url(${sparklesUrl})`,
    maskImage: `url(${sparklesUrl})`,
    WebkitMaskSize: 'contain',
    maskSize: 'contain',
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    animation: '$sparklePulse 1.6s ease-in-out infinite'
  },
  // 200%/200% with -50% offset keeps the gradient covering the masked
  // area at every rotation angle (the rotated 72x72 bbox is well past
  // the 36x36 mask diagonal of ~51px).
  sparkleGradient: {
    position: 'absolute',
    width: '200%',
    height: '200%',
    top: '-50%',
    left: '-50%',
    background: 'linear-gradient(0deg, var(--palette-blue-200) 0%, var(--palette-blue-500) 50%, var(--palette-blue-300) 100%)',
    animation: '$rotateGradient 3s linear infinite'
  },
  '@keyframes sparklePulse': {
    '0%, 100%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.05)' }
  },
  '@keyframes rotateGradient': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' }
  },
  message: {
    fontSize: 'var(--font-size-md)',
    color: 'var(--text-color)'
  },
  sweepIn: {
    display: 'inline-block',
    WebkitMaskImage: 'linear-gradient(90deg, black 0%, black 33%, transparent 67%, transparent 100%)',
    maskImage: 'linear-gradient(90deg, black 0%, black 33%, transparent 67%, transparent 100%)',
    WebkitMaskSize: '300% 100%',
    maskSize: '300% 100%',
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    animation: '$textSweep 0.9s ease-out forwards'
  },
  '@keyframes textSweep': {
    from: {
      WebkitMaskPosition: '100% 0',
      maskPosition: '100% 0'
    },
    to: {
      WebkitMaskPosition: '0% 0',
      maskPosition: '0% 0'
    }
  }
}))

/**
 * Loading visual for Portfolio Analysis: a magic-sparkles silhouette filled
 * with a rotating linear gradient (Denali AI purple palette) that pulses
 * gently, plus a status message to its right that animates in
 * left-to-right. The message cycles through randomized
 * two-phase pools while `active` is true. See `hooks/useLoadingMessage`
 * for the cycling logic.
 *
 * @param {{ active: boolean }} props
 */
export default function LoadingMessage ({ active }) {
  const classes = useStyles()
  const message = useLoadingMessage(active)
  return (
    <div className={classes.root}>
      <div className={classes.sparkleContainer}>
        <div className={classes.sparkleGradient} />
      </div>
      <div className={classes.message}>
        <span key={message || 'idle'} className={classes.sweepIn}>{message || ''}</span>
      </div>
    </div>
  )
}

LoadingMessage.propTypes = {
  active: PropTypes.bool.isRequired
}
